import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Alert, Badge, Button, Group, Paper, PasswordInput, Stack, Text, TextInput, Tooltip } from "@mantine/core"
import { useCallback, useState } from "react"
import type { useCompanyApi } from "../../../hooks/useCompanyApi"
import { testTranscriptionProvider } from "../../../lib/api"
import { extractErrorMessage } from "../../../lib/api/errors"
import { TRANSCRIPTION_QUALITY, TRANSCRIPTION_QUALITY_TIERS } from "./constants"

interface IProps {
  name: string
  label: string
  settingKey: string
  currentKey: string
  /** A secret is stored server-side but masked — the client never holds it.
   *  Shows a "Configured" badge + placeholder and disables Test (you can't
   *  test a key you don't have). Cleared the moment the user types a new key. */
  configured?: boolean
  onKeyChange: (key: string) => void
  onSave: () => void
  saving: boolean
  company: ReturnType<typeof useCompanyApi>
  /** Extra input for Azure region */
  regionKey?: string
  currentRegion?: string
  onRegionChange?: (region: string) => void
  /** Render only the key controls (no outer card / provider header). Used when
   *  the caller already shows the provider name + status on a row and just wants
   *  the expandable key entry underneath. */
  bare?: boolean
}

export function TranscriptionProviderCard({
  name,
  label,
  currentKey,
  configured = false,
  onKeyChange,
  onSave,
  saving,
  company,
  regionKey,
  currentRegion,
  onRegionChange,
  bare = false,
}: IProps): React.JSX.Element {
  const { i18n } = useLingui()
  const quality = TRANSCRIPTION_QUALITY[name]
  const tierMeta = quality ? TRANSCRIPTION_QUALITY_TIERS[quality.tier] : null
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ valid: boolean; error?: string } | null>(null)

  // A configured key with no freshly-typed replacement: the saved value is
  // unknown to the client, so Test is meaningless and Save is a no-op.
  const isConfiguredUnchanged = configured && currentKey.trim() === ""

  const handleTest = useCallback(async (): Promise<void> => {
    if (!currentKey.trim() || !company) return
    setTesting(true)
    setTestResult(null)
    try {
      const result = await testTranscriptionProvider(company, name, currentKey)
      setTestResult(result)
    } catch (err) {
      setTestResult({ valid: false, error: extractErrorMessage(err) })
    } finally {
      setTesting(false)
    }
  }, [name, currentKey, company])

  const controls = (
    <Stack gap="sm">
      <Group align="end" gap="sm">
        <PasswordInput
          label={i18n._(msg`API key`)}
          placeholder={
            isConfiguredUnchanged
              ? i18n._(msg`•••• saved key — enter a new one to replace`)
              : i18n._(msg`Enter ${label} API key`)
          }
          value={currentKey}
          onChange={(e) => onKeyChange(e.currentTarget.value)}
          style={{ flex: 1 }}
          size="sm"
        />
        <Button
          size="sm"
          variant="light"
          onClick={handleTest}
          loading={testing}
          disabled={!currentKey.trim() || !company}
        >
          <Trans>Test</Trans>
        </Button>
        <Button size="sm" onClick={onSave} loading={saving} disabled={!currentKey.trim()}>
          <Trans>Save</Trans>
        </Button>
      </Group>
      {regionKey && onRegionChange !== undefined && (
        <TextInput
          label={i18n._(msg`Region`)}
          placeholder={i18n._(msg`e.g. westeurope`)}
          value={currentRegion ?? ""}
          onChange={(e) => onRegionChange(e.currentTarget.value)}
          size="sm"
        />
      )}
      {/* In bare mode the row header carries no live test result, so surface it here. */}
      {bare && testResult && (
        <Badge color={testResult.valid ? "success" : "danger"} variant="dot" w="fit-content">
          {testResult.valid ? <Trans>Connected</Trans> : <Trans>Error</Trans>}
        </Badge>
      )}
      {testResult?.error && (
        <Alert color="danger" variant="light">
          {testResult.error}
        </Alert>
      )}
    </Stack>
  )

  if (bare) return controls

  return (
    <Paper withBorder p="md">
      <Group justify="space-between" mb="sm">
        <Group gap="xs">
          <Text fw={600}>{label}</Text>
          {quality && tierMeta ? (
            <Tooltip
              label={i18n._(msg`From internal Dutch-language meeting benchmarks (Jun 2026)`)}
              withArrow
              multiline
              w={240}
            >
              <Badge color={tierMeta.color} variant="light" size="sm" style={{ cursor: "default" }}>
                {i18n._(tierMeta.label)}
              </Badge>
            </Tooltip>
          ) : null}
        </Group>
        {testResult ? (
          <Badge color={testResult.valid ? "success" : "danger"} variant="dot">
            {testResult.valid ? <Trans>Connected</Trans> : <Trans>Error</Trans>}
          </Badge>
        ) : isConfiguredUnchanged ? (
          <Badge color="success" variant="light">
            <Trans>Configured</Trans>
          </Badge>
        ) : !currentKey ? (
          <Badge color="gray" variant="outline">
            <Trans>Not configured</Trans>
          </Badge>
        ) : null}
      </Group>
      {quality ? (
        <Text size="xs" c="dimmed" mb="sm">
          {i18n._(quality.note)}
        </Text>
      ) : null}
      {controls}
    </Paper>
  )
}
